import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

export type BzInputSize = 'sm' | 'md';

/**
 * `outlined` is the default field: a white box with a visible border.
 * `filled` is the design's Search Field chrome — a grey fill with no border at
 * rest, gaining the border on hover and lifting to white while focused. It is
 * an appearance rather than a `bz-search-input` detail because the fill *is*
 * how that field shows focus, and a component cannot reach into another
 * component's custom properties to arrange that.
 */
export type BzInputAppearance = 'outlined' | 'filled';

/**
 * A single-line text field.
 *
 * The value is always a string, including for `type="number"` — that is what
 * the DOM gives back, and quietly coercing it would hide the difference
 * between an empty field and a zero. A caller that wants a number parses it.
 *
 * Content projected into the `[bzInputPrefix]` and `[bzInputSuffix]` slots
 * sits inside the field's border, which is what makes a search icon or a unit
 * label line up with the text instead of floating next to the box.
 */
@Component({
  selector: 'bz-input',
  templateUrl: './bz-input.html',
  styleUrl: './bz-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BzInput),
      multi: true,
    },
  ],
})
export class BzInput implements ControlValueAccessor {
  readonly value = model('');
  readonly disabled = model(false);
  readonly type = input<'text' | 'email' | 'password' | 'search' | 'tel' | 'url' | 'number'>('text');
  readonly placeholder = input('');
  readonly size = input<BzInputSize>('md');
  readonly appearance = input<BzInputAppearance>('outlined');
  readonly readonly = input(false);
  readonly invalid = input(false);

  /** Only needed when the field has no visible label next to it. */
  readonly ariaLabel = input('');

  protected readonly wrapperClass = computed(() => {
    const classes = ['bz-input', `bz-input--${this.size()}`, `bz-input--${this.appearance()}`];
    if (this.invalid()) {
      classes.push('bz-input--invalid');
    }
    if (this.disabled()) {
      classes.push('bz-input--disabled');
    }
    return classes.join(' ');
  });

  private propagateChange: (value: string) => void = () => undefined;
  private propagateTouched: () => void = () => undefined;

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.propagateChange(next);
  }

  protected markTouched(): void {
    this.propagateTouched();
  }
}
